import React from 'react';

import Highcharts, { Options } from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import highchartsAccessibility from 'highcharts/modules/accessibility';
import highchartsExportData from 'highcharts/modules/export-data';
import highchartsExporting from 'highcharts/modules/exporting';
import { ReportsResult } from '../../context/ReportsResultContext';

highchartsAccessibility(Highcharts);
highchartsExporting(Highcharts);
highchartsExportData(Highcharts);

type PieChartProps = {
  stats: ReportsResult['stats'];
};

export function PieChart(props: PieChartProps) {
  const series = React.useMemo(
    () => [
      {
        type: 'pie' as const,
        name: 'Applications',
        innerSize: '60%',
        data: props.stats.map((stat) => ({
          y: stat.value,
          name: stat.title,
          color: stat.color,
        })),
        showInLegend: false,
        dataLabels: {
          enabled: false,
        },
        states: {
          hover: {
            brightness: 0.05,
          },
          inactive: {
            opacity: 0.9,
          },
        },
      },
    ],
    [props.stats]
  );

  const options: Options = {
    plotOptions: {
      pie: {
        allowPointSelect: true,
        cursor: 'pointer',
        dataLabels: {
          enabled: false,
        },
        showInLegend: false,
        states: {
          hover: {
            brightness: 0.05,
          },
          inactive: {
            opacity: 0.9,
          },
        },
      },
      series: {
        animation: false,
      },
    },
    chart: {
      type: 'pie',
      animation: false,
      backgroundColor: 'transparent',
      height: 500,
    },
    credits: { enabled: false },
    title: { text: '' },
    accessibility: { announceNewData: { enabled: true } },
    legend: {
      enabled: false,
    },
    tooltip: {
      useHTML: true,
      backgroundColor: 'transparent',
      borderWidth: 0,
      shadow: false,
      padding: 0,
      style: {
        color: '#222',
        fontSize: '14px',
        fontFamily: 'Open Sans',
        fontWeight: '600',
        pointerEvents: 'none',
      },
      formatter: createTooltipFormatter(),
    },
    exporting: { enabled: false },
    series: series,
  };

  return (
    <div>
      <HighchartsReact highcharts={Highcharts} options={options}></HighchartsReact>
    </div>
  );
}

const createTooltipFormatter = () => {
  return function (this: Highcharts.TooltipFormatterContextObject) {
    const { point } = this;
    return PieChartTooltip({
      color: point.color as string,
      name: point.name as string,
      value: point.y as number,
    });
  };
};

export const PieChartTooltip = ({ color, name, value }: { color: string; name: string; value: number }) => {
  return `<div style="background: #fff; border-radius: 16px; box-shadow: 0 2px 8px 0 rgba(50, 50, 93, 0.10); padding: 10px 18px; display: flex; align-items: center; gap: 8px; min-width: 0;">
            <span style="display:block;width:12px;height:12px;border-radius:50%;background:${color};margin-right:8px;"></span>
            <span style="font-weight:bold;color:#222;white-space:nowrap;">${name}: ${value}</span>
          </div>`;
};
